import { useContext } from "react";
import { LanguageContext } from "../../../context/languageContext";
import { formatNumber } from "../../../util/format";
import { ProductContext } from "../../../context/product-context/productContext";

interface FormatterProperties {
  thousandSeparator?: string;
  suffix?: string;
  prefix?: string;
  currencyPositionAfter?: boolean;
  withCurrency?: boolean;
}

interface FormatNumber {
  formatStringAsAmount: (
    value: string,
    configOverride?: FormatterProperties,
  ) => string;
  formatNumberAsAmount: (
    value: number,
    configOverride?: FormatterProperties,
  ) => string;
}

export function useFormatNumber(): FormatNumber {
  const { language } = useContext(LanguageContext);
  const { selectedProduct } = useContext(ProductContext);

  const getConfig = (configOverride?: FormatterProperties) => {
    let config: FormatterProperties = {
      thousandSeparator: " ",
      suffix: ".–",
      currencyPositionAfter: false,
      withCurrency: false,
    };

    switch (language) {
      case "de_CH":
        config = {
          thousandSeparator: " ",
          suffix: ".–",
          currencyPositionAfter: false,
        };
        break;
      case "fr_CH":
        config = {
          thousandSeparator: " ",
          currencyPositionAfter: true,
        };
        break;
      case "it_CH":
        config = {
          thousandSeparator: " ",
          currencyPositionAfter: false,
        };
        break;
      case "en_CH":
        config = {
          thousandSeparator: ",",
          currencyPositionAfter: false,
        };
        break;
    }

    return configOverride ? { ...config, ...configOverride } : config;
  };

  const formatStringAsAmount = (
    value: string,
    configOverride?: FormatterProperties,
  ): string => {
    const { thousandSeparator, prefix, suffix } = getConfig(configOverride);
    return formatNumber(value, thousandSeparator, prefix, suffix);
  };

  const formatNumberAsAmount = (
    value: number,
    configOverride?: FormatterProperties,
  ) => {
    const {
      withCurrency,
      thousandSeparator,
      suffix,
      prefix,
      currencyPositionAfter,
    } = getConfig(configOverride);

    const resolvedCurrency = withCurrency ? selectedProduct?.currency : "";

    const formattedNumber = formatNumber(
      String(value),
      thousandSeparator,
      prefix,
      suffix,
    );

    return currencyPositionAfter
      ? `${formattedNumber} ${resolvedCurrency}`.trim()
      : `${resolvedCurrency} ${formattedNumber}`.trim();
  };

  return {
    formatStringAsAmount,
    formatNumberAsAmount,
  };
}
